import { useCallback, useState } from 'react';
import type { FactorType } from '@/api/generated';
import { ChallengeStatus, FlowStatus } from '@/api/generated';
import {
  submitMfaResponse,
  getAlternatives,
  chooseAuthenticator,
} from '@/services/apis/mfa-orchestrator.api';
import type {
  MfaOrchestratorProps,
  MfaOrchestratorState,
  UseMfaOrchestratorReturn,
} from './use-mfa-orchestrator.types';

const RETRY_CHALLENGE_STATUSES: readonly ChallengeStatus[] = [
  ChallengeStatus.FAILED,
  ChallengeStatus.REJECTED,
  ChallengeStatus.RETRY_REQUIRED,
];

/**
 * Hook for handling MFA orchestration flow
 * Manages submission of factor responses and flow progression
 *
 * Responsibilities:
 * - Submit factor responses to orchestrator
 * - Handle next step transitions
 * - Manage flow completion
 * - Error handling with callbacks
 */
export const useMfaOrchestrator = ({
  initialStep,
  initialStepData,
  initialHasAlternative,
  onFlowComplete,
  onError,
}: MfaOrchestratorProps): UseMfaOrchestratorReturn => {
  const [state, setState] = useState<MfaOrchestratorState>({
    currentStep: initialStep,
    status: FlowStatus.IN_PROGRESS,
    isSubmitting: false,
    errorMessage: '',
    currentStepData: initialStepData ?? null,
    submitCount: 0,
    hasAlternative: initialHasAlternative ?? false,
    showAlternatives: false,
    alternatives: [],
    isFetchingAlternatives: false,
  });

  /**
   * Submit response to orchestrator
   * Handles flow progression and completion
   */
  const submitResponse: UseMfaOrchestratorReturn['submitResponse'] =
    useCallback(
      async (
        payload: Record<string, unknown>
      ): Promise<FlowStatus | ChallengeStatus> => {
        // Prevent duplicate submissions
        if (state.isSubmitting) {
          return Promise.resolve(FlowStatus.IN_PROGRESS);
        }

        setState(prev => ({
          ...prev,
          isSubmitting: true,
          errorMessage: '',
        }));

        try {
          const response = await submitMfaResponse(payload);

          // Handle flow progression
          if (response.currentStep) {
            const currentStep = response.currentStep;
            // Non-retry status = flow advanced; hides the first-step back arrow.
            const isRetry = RETRY_CHALLENGE_STATUSES.includes(
              currentStep.status
            );
            setState(prev => ({
              ...prev,
              status: response.status,
              currentStep: currentStep.factorType,
              isSubmitting: false,
              challengeStatus: currentStep.status,
              currentStepData: currentStep,
              hasAlternative: response.hasAlternative ?? false,
              submitCount: isRetry ? prev.submitCount : prev.submitCount + 1,
            }));

            // Return the challenge status
            return Promise.resolve(currentStep.status);
          } else {
            // Flow is complete (no more steps)
            console.info(
              'MFA Orchestrator: Flow complete, status:',
              response.status
            );
            setState(prev => ({
              ...prev,
              status: response.status,
              isSubmitting: false,
            }));

            // Call onFlowComplete when flow is complete
            if (response.status === FlowStatus.COMPLETED) {
              onFlowComplete(response.status);

              return Promise.resolve(response.status);
            }

            // Treat FAILED status as an error
            if (response.status === FlowStatus.FAILED) {
              console.error(
                'MFA Orchestrator: Flow failed with status:',
                response.status
              );
              const errorObj = new Error('Authentication flow failed');

              setState(prev => ({
                ...prev,
                status: response.status,
                isSubmitting: false,
                errorMessage: errorObj.message,
              }));

              onError?.(errorObj);

              return Promise.resolve(response.status);
            }

            return Promise.resolve(response.status);
          }
        } catch (error) {
          const errorObj =
            error instanceof Error
              ? error
              : new Error('Failed to submit response');

          setState(prev => ({
            ...prev,
            isSubmitting: false,
            errorMessage: errorObj.message,
          }));

          onError?.(errorObj);

          return Promise.resolve(FlowStatus.IN_PROGRESS);
        }
      },
      [state.isSubmitting, onFlowComplete, onError]
    );

  /**
   * Fetch available alternative authenticators for current step
   */
  const fetchAlternatives = useCallback(async () => {
    setState(prev => ({ ...prev, isFetchingAlternatives: true }));

    try {
      const response = await getAlternatives();
      setState(prev => ({
        ...prev,
        alternatives: response.alternatives,
        showAlternatives: true,
        isFetchingAlternatives: false,
      }));
    } catch (error) {
      console.error('MFA Orchestrator: Failed to fetch alternatives:', error);
      const errorObj =
        error instanceof Error
          ? error
          : new Error('Failed to load alternatives');
      setState(prev => ({
        ...prev,
        isFetchingAlternatives: false,
        errorMessage: errorObj.message,
      }));
      onError?.(errorObj);
    }
  }, [onError]);

  /**
   * Switch to an alternative authenticator
   * Calls backend to change factor and updates state with new factor
   */
  const switchToAlternative = useCallback(
    async (factorType: string) => {
      setState(prev => ({ ...prev, isSubmitting: true }));

      try {
        const response = await chooseAuthenticator(factorType as FactorType);

        if (response.currentStep) {
          const currentStep = response.currentStep;
          setState(prev => ({
            ...prev,
            currentStep: currentStep.factorType,
            currentStepData: currentStep,
            status: response.status,
            hasAlternative: response.hasAlternative ?? false,
            showAlternatives: false,
            alternatives: [],
            isSubmitting: false,
            errorMessage: '',
          }));
        } else {
          // Unexpected: no current step after choosing alternative
          throw new Error(
            'No current step returned after choosing alternative'
          );
        }
      } catch (error) {
        console.error(
          'MFA Orchestrator: Failed to switch authenticator:',
          error
        );
        const errorObj =
          error instanceof Error
            ? error
            : new Error('Failed to switch authenticator');
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          errorMessage: errorObj.message,
        }));
        onError?.(errorObj);
      }
    },
    [onError]
  );

  /**
   * Close alternatives view and return to current factor
   */
  const closeAlternatives = useCallback(() => {
    setState(prev => ({
      ...prev,
      showAlternatives: false,
      alternatives: [],
    }));
  }, []);

  return {
    state,
    submitResponse,
    fetchAlternatives,
    switchToAlternative,
    closeAlternatives,
  };
};
